import api, { formatApiError } from "./api";
import { toast } from "sonner";

/**
 * Downloads the rental agreement PDF for a booking. Uses the shared axios
 * instance so httponly-cookie auth (`withCredentials: true`) is applied,
 * fixes the previous `<a href>` approach that (on cross-origin deploys)
 * dropped the credentials and hit the API without auth → 403.
 *
 * If the deploy ever migrates to localStorage-based Bearer tokens, the
 * shared axios instance is the only place that needs to change — every
 * caller stays this simple.
 */
export async function downloadAgreementPdf(bookingId) {
  try {
    const res = await api.get(`/bookings/${bookingId}/agreement.pdf`, {
      responseType: "blob",
      // Prevent the JSON-guard interceptor from rejecting the PDF binary
      // payload — this endpoint intentionally returns application/pdf.
      transformResponse: [(d) => d],
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `driveline-agreement-${bookingId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Free the object URL on the next tick — Firefox needs the click to
    // register first.
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  } catch (err) {
    // Blob error responses need to be text-decoded to extract our JSON envelope.
    let detail = err?.message || "Failed to download PDF";
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed?.detail || detail;
      } catch { /* keep default detail */ }
    } else if (err?.response?.data?.detail) {
      detail = formatApiError(err.response.data.detail);
    }
    toast.error("Could not download PDF", { description: detail });
    throw err;
  }
}
